import { getLink } from '../../../../utils/utils';

import author from './author';

const content = {
  rus: (
    `
    <div style="text-align: justify;">
      <p class="mb-3">
        Язык эсперанто был создан для целей языкового общения в 1887 г.
      </p>
      
      <p class="mb-3">
        Созданные человеком искусственные языки – это системы знаков, применяемые там, 
        где применение естественного языка затруднено или невозможно. 
        Это, например, языки программирования, информационные языки, терминологические языки науки. 
        Отдельный класс составляют искусственные международные языки. 
        Исследованием международных искусственных языков занимается наука интерлингвистика.
      </p>
      
      <p class="mb-3">
        Всего существует около 1000 проектов искусственных (т.е. созданных отдельными мыслителями, учёными или коллективами учёных) 
        языков[1]. 
        Большинство (ок. 600) из них было предложено уже после появления эсперанто; 
        однако изо всех подобных языков только эсперанто выдержал проверку временем и до сих пор является живым языком, 
        на котором создана обширная литература, как художественная, 
        так и научно-техническая, проводятся конгрессы, семинары, фестивали и другие мероприятия, 
        куда съезжаются тысячи человек со всего света.
      </p>
      
      <p class="mb-3">
        Чем же принципиально отличается язык эсперанто от других лингвопроектов?
      </p>
      
      <p class="mb-3">
        Прежде всего, это <b><i>живой, разговорный</i></b> язык. Языковые системы такого типа 
        (международный + искусственный + коммуникативно реализованный) в 
        интерлингвистике называются <i>плановыми языками</i>[2]. 
        Плановые языки могут быть <i>априорными</i> или <i>апостериорными</i>, 
        т. е. или независимыми от материала естественных языков, самостоятельно создающими свою лексику, 
        или заимствующими для создания своей лексики материал естественных языков. 
        Априорные языки (философские языки Уилкинза, Бермудо, Сотоса Очандо) ввиду своей сложности не могли быть использованы для живого общения. 
        В Х1Х веке, когда Земля покрылась сетью железных дорог были изобретены радио и телеграф, 
        стала особенно насущной потребность в быстром  и надёжном, простом в изучении средстве общения между различными странами. 
        Появилось множество проектов международных языков, большей частью апостериорных. 
        Так, в 1879 г. был предложен первый плановый язык, получивший распространение в мире – <i>волапюк</i>. 
        Но его жизнь продлилась всего 40 лет, до 1916 года, года смерти его автора. 
        Этот язык не выработал социальных механизмов своего функционирования, и вместе со своим поколением сошёл со сцены.
      </p>
      
      <p class="mb-3">
        Появившийся в 1887 г. эсперанто, являясь языком апостериорным, имеет, тем не менее, априорные ингредиенты: 
        многие местоимения, местоименные наречия, окончания частей речи, некоторые аффиксы. 
        Такие языки, заимствующие большую часть своего материала из естественных языков, 
        но организующие этот материал по собственным законам, называются <i>автономными</i>[3]. 
        Эсперанто был первым из автономных языков.
      </p>
      
      <p class="mb-3">
        Кроме того, в его основе лежит принцип <i>социальной эволюции</i> языка. 
        На титульном листе своей «Первой книги», вышедшей 16 июля 1887 года, создатель эсперанто доктор Л. Заменгоф провозгласил: 
        «Интернациональный язык, подобно всякому национальному языку, составляет достояние общественное, 
        и от всяких личных прав на него автор навсегда отказывается»[4]. 
        Это значит, что язык будет жить и развиваться в устах людей, говорящих на нём, как и любой национальный язык. 
        Именно в живом общении и употреблении эсперанто стал живым языком и вот уже 118 лет существует в мире, 
        несмотря на две мировые войны и множество других проблем, разделяющих человечество. 
      </p>
      
      <p class="mb-3">
        Эсперанто опирается ещё на один принцип, также высказанный Л. Заменгофом: на <i>принцип фундаментальности</i>. 
        Гарантировать стабильность языка в пространстве и времени может только наличие некоторой общеобязательной основы, 
        в роли которой выступает книга Л. Заменгофа «Фундамент эсперанто», 
        включающая в себя всю официально установленную грамматику из 16 правил, 
        42 параграфа упражнений как образец эсперантского стиля и основной словарь[5]. 
        Это основа языка, которая никогда не должна меняться. 
        «Язык, без сомнения, может и должен постоянно улучшаться и совершенствоваться, 
        но это совершенствование будет идти не путём произвольной разрушительной ломки и изменения, а естественным путём – 
        путём постепенного распространения новых слов, если они необходимы людям, говорящим на этом языке, и принимаются ими», - 
        говорил Л. Заменгоф[6].
      </p>
      
      <p class="mb-3">
        За 118 лет своего существования эсперанто претерпел некоторую эволюцию; как и в любом живом языке, в нём уже есть и архаизмы, 
        и неологизмы. 
        Но он по-прежнему выполняет свою функцию международного вспомогательного языка. 
        Эсперанто не стремится заменить собою какой-либо современный национальный язык; 
        он претендует на роль средства международного общения, т. е. служить для равноправного общения людей, 
        говорящих на разных языках. 
        В современном мире существует довольно широкое эсперанто-движение, как одна из сил, 
        действующих в направлении взаимопонимания различных культур, их сближения и обмена культурными ценностями.
      </p>
      
      <p class="mb-3">
        Литература, культура, традиции малых стран, находящихся за пределами Европы или принадлежащих к иным, 
        кроме индоевропейской, языковым семьям, имеют мало шансов быть представленными на международном уровне. 
        Традиционный европоцентризм, хотя и резко сдал свои позиции в ХХ веке, всё же ещё чувствуется в отношении т. н. 
        «больших» наций к малым. 
        Переводы с языков «малых» на «большие», с неевропейских на европейские всё ещё весьма редки в современном мире. 
        Замечательным исключением из этого правила являлась советская переводческая практика, 
        когда на русский язык переводились произведения на языках народов СССР, Азии, Африки… 
        Но это было и осталось исключением. 
        Ведь недаром ещё Достоевский говорил о «всемирной душе» русского человека, 
        которая открыта всем и перенимает всё лучшее в культурах других народов.
      </p>
      
      <p class="mb-3">
        Однако существует такой феномен, как <i>международный</i> язык эсперанто, 
        который представляет собой прекрасный субстрат для таких интеркультурных взаимовлияний и взаимообменов. 
        Язык очень точный в средствах выражения (что немаловажно для практического перевода), лёгкий, красивый и благозвучный, 
        он обладает ещё одной особенностью, делающей его уникальным: он одинаково близок как европейским, так и азиатским языкам. 
        Его лексика построена на основе латыни и включает в себя приблизительно 60% романских, 30% германских, 6% славянских корней, 
        т. е. является чисто индоевропейской[7]. 
        Однако структура языка, его морфология построены совершенно по другому принципу, 
        свойственному восточным (тюркским, японскому и т.д.) языкам. 
        Эсперанто – язык агглютинативный, в отличие от европейских флективных. 
        Поэтому он одинаково доступен и близок и европейцу – по словарному запасу, и азиату – по внутренней структуре языка. 
        Идея именно такой организации этого искусственно созданного языка была гениальным озарением его создателя, 
        польского врача-окулиста Л. Заменгофа, т. к. во время создания языка эсперанто (1887 год) восточные языки ещё не были «модными», 
        их знало в Европе только очень ограниченное число специалистов, и Азия представлялась в Европе диким непросвещённым краем, 
        практически вне круга человечества, который надо было «цивилизовать», чтобы поднять до контактов с собой. 
        Ср. Р. Киплинг, «Бремя белого человека»:
      </p>   


      
      <div class="mb-4 kanto">
        <div class="no-inner-indent flex-center">
          <p>
            «Неси это гордое Бремя<br/>
            Родных сыновей пошли<br/>
            На службу тебе подвластным<br/>
            Народам на край земли, - <br/>
            На каторгу ради угрюмых<br/>
            Мятущихся дикарей,<br/>
            Наполовину бесов,<br/>
            Наполовину людей!»  <br/>
            <span style="width: 100%;display: inline-block;text-align: right;">(пер. А. Сергеева)</span>
          </p>
        </div>
      </div>
      


      <p class="mb-3">
        Таким было вполне обычное отношение Европы к Азии в 1897 году, через 10 лет после создания языка эсперанто. 
        О разговоре на равных, о взаимном признании и уважении не могло быть и речи.
      </p>
      
      <p class="mb-3">
        Поэтому создание пространства, где на равных могли бы встретиться Запад и Восток, которым ранее, по словам того же Киплинга, 
        было «вместе не сойтись» - это большое завоевание эсперанто и осуществление его культурной миссии посредника, 
        одинаково близкого и понятного и той, и другой стороне.
      </p>
      
      <p class="mb-3">
        Одним из средств развития и обогащения языка служит художественная литература, 
        развитию которой уделял большое внимание ещё сам Л. Заменгоф. 
        Пока не будем касаться оригинальной литературы, создаваемой на этом языке; обратим внимание на переводы. 
        Много антологий национальной поэзии создано на языке эсперанто: существуют чешская, венгерская, сербская, английская, 
        китайская и многие другие. 
        Переводы лучших произведений своей литературы делали поэты-эсперантисты, с родного национального на родной эсперанто. 
        Теоретически это обеспечивает высокую степень адекватности по сравнению с обычными переводами, которые делаются с чужого языка на свой 
        (в реальности всё, конечно, зависит от поэтического таланта переводчика)
      </p>
      
      <p class="mb-3">
        Ещё одно свойство эсперанто как вспомогательного языка для переводов было изучено в специальных исследованиях[8]. 
        Одну фразу из «Повести о том, как поссорились Иван Иванович с Иваном Никифоровичем» Н. В. Гоголя 
        было предложено перевести с одного языка на другой ряду профессиональных переводчиков, 
        учителей иностранного языка и студентов языковых факультетов нескольких московских вузов. 
        Начальную фразу: «Она сплетничала и ела варёные бураки по утрам, и отлично хорошо ругалась, - 
        и при всех этих разнообразных занятиях лицо её ни на минуту не изменяло своего выражения, 
        что обыкновенно могут показывать одни только женщины», - требовалось переводить с одного языка на другой по цепочке: 
        с русского на английский, с английского на немецкий, с немецкого на японский… и т. д., на протяжении двух десятков языков. 
        Среди них были и языки «вежливые», как японский, где «сплетничала» заменили на «болтала», и «цветистые» как арабский, 
        где «ругалась» заменили на «извергала ужасные проклятья». 
        На этапе индонезийского языка пропало местоимение «она», т. к. в этом языке «он» и «она» выражаются одним и тем же словом, 
        и в дальнейшем действовали уже две персоны: один ругался, а другой болтал. 
        На языке йоруба «свёкла», уже ранее заменённая на «корнеплоды», превратилась просто в «фрукты», 
        а «болтовня» превратилась в «битьё в литавры». 
        «Литавры» затем были заменены «тамтамом», а «фрукты» - «компотом». 
        Под влиянием «тамтама» где-то на промежуточных этапах появившийся было «дом» превратился в «хижину»…
      </p>
      
      <p class="mb-3">
        В итоге начальная фраза, снова переведённая на русский, звучала так: 
        «Выпив компот, она выбросила из хижины всё старьё, а он радостно забил в тамтам».
      </p>
      
      <p class="mb-3">
        От таких перипетий избавил бы язык эсперанто, который, благодаря своей дискретной структуре, 
        является совершенно точным субстратом для перевода: 
        текст не меняется даже при многократном переводе с эсперанто на национальные языки и с национальных снова на эсперанто. 
        Именно в роли языка-посредника, субстрата для перевода с одного языка на другой эсперанто был предложен для т. н. 
        «разделённого перевода» в Европейском Сообществе в 1988-1990 гг. 
        Примерно с этого времени изменилось направление исследований в области машинного перевода: программы, 
        основанные на совокупности правил, стали заменяться огромными базами данных, «параллельными корпусами» текстов на двух языках, 
        где перевод всегда шёл рядом с оригиналом, 
        чей контекст позволял учитывать малейшие нюансы языка. 
        Один из авторов проекта «разделённого перевода» (DLT) Виктор Садлер[9] опубликовал книгу о данном семантическом методе. 
        К сожалению, проект DLT не увенчался успехом, как и другие проекты машинного перевода: Eurotra, Rosetta, IBM и др., хотя, 
        например, на проект Eurotra было затрачено в 10 раз больше средств[10], чем на проект DLT. 
        Причина всего этого одна: отсутствие таких колоссальных корпусов текстов, которые позволят реализовать эти проекты.
      </p>
      
      <p class="mb-3">
        Итак, эсперанто благодаря особенностям своей структуры может быть идеально точным средством для переводов с одного языка на другой. 
        Так, в октябре 1997 года в Одессе была издана книжечка моих переводов древнейшей антологии японской поэзии «Ман-ё-сю». 
        Переводы были сделаны не с японского языка, которого я не знаю, 
        а со сборника избранных переводов из этой антологии на эсперанто, принадлежащих перу писателя, 
        переводчика и теоретика языка эсперанто Кензи Оссака (1898-1972). 
        Книга его переводов вышла в Токио в 1958 г. к 60-летию со дня его рождения и 40-летию эсперантской деятельности. 
        Мне эту книгу подарили в Японском Эсперанто-Институте в Токио в 1995 году, 
        когда я была в Японии по приглашению эсперанто-клуба нашего побратима – города Иокогамы. 
        Восхищённая этими замечательными стихами, я тут же попыталась перевести их. 
        Особенность этого перевода в том, что он осуществлён сначала с родного японского на родной эсперанто, 
        а затем с родного эсперанто на родной русский. Это не единственный опыт такого рода в мире; 
        в уже цитированной статье М.Гросжан говорится: «Преимущество эсперанто в том, что переводчик является носителем языка. 
        Кавабата переведён на эсперанто японцем, Достоевский – русским. 
        Переводчик находится в ситуации прямой передачи оригинала и может создать, благодаря гибкости языка, текст, 
        который так же адекватен языку оригинала, как если бы оба языка были его родными»[11]. 
        Таким образом, каждый человек, приобщаясь к эсперанто-деятельности, становится членом всечеловеческого сообщества, 
        на равных воспринимая чилийца, тоголезца или японца, 
        но сохраняя при этом свою собственную национальную культуру со всеми её отличиями; и именно потому, что сохраняет своё, 
        он может воспринимать чужое.
      </p>
      
      <p class="mb-3">
        Известный современный философ и культуролог Умберто Эко в книге «В поисках совершенного языка»[12] исследует именно эту идею, 
        которую несёт в себе язык эсперанто, поднимая её на планетарный уровень. 
        Мы знаем У. Эко как автора книги «Имя розы», но в западном мире его ценят именно как теоретика и мыслителя. 
        У. Эко отнюдь не являлся приверженцем эсперанто; 
        но в процессе своей работы над книгой об эволюции языков и о самой идее межъязыкового понимания он познакомился с эсперанто и, 
        к собственному удивлению, увидел в нём чисто языковые преимущества и высокую моральную идею, 
        отвечающую самым насущным требованиям человечества. 
        Это – язык языковой демократии, к которой только подходит современное общество, в отличие от языкового империализма, 
        который в нём сейчас процветает. 
        Эти идеи отражены в Пражском манифесте движения за международный язык эсперанто, подписанном уже десятками тысяч человек во всём мире[13]. 
        Стремясь к предоставлению равных языковых прав всем народам и культурам, к поддержке разноязычия, к свободе самовыражения, 
        общения и сотрудничества людей, эсперантисты являются пионерами в освоении международного лингвистического пространства.
      </p>
      
      <p class="mb-3">
        Нужно заметить, что в «Табеле о внятности языков», опубликованном ООН, 
        эсперанто стоит на втором месте, уступая только итальянскому; английский же там только седьмой[14]. 
      </p>
      
      <p class="mb-3">
        Как сказал Макс Женевуа, постоянный секретарь Французской Академии, «Эсперанто совсем не однообразный язык; наоборот, 
        это – язык естественный и гибкий, способный выразить наиболее тонкие нюансы мысли и чувства». 
        Его поддерживает Хайнц Зерьес, руководитель Брюссельской терминологической группы: «Эсперанто из всех живых языков – то же самое, 
        что метрическая система для традиционных мер»[15].
      </p>
      
      <p class="mb-3">
        Всё сказанное подтверждает ту мысль, что язык эсперанто является уникальным, 
        истинно демократическим средством межкультурной коммуникации. 
        К сожалению, этот язык и его особенности мало известны специалистам, 
        и данный доклад служит целям первичного ознакомления с этим важным языковым феноменом.
      </p>



      
      <p>
        БИБЛИОГРАФИЯ
      </p>
      <ol>
        <li>С. Н. Кузнецов, «Теоретические основы интерлингвистики», М, изд. Ун-та Дружбы народов, 1987, с. 44 </li>
        <li>Там же, с. 66-67</li>
        <li>Там же, стр. 106</li>
        <li>Доктор Эсперанто, «Международный язык, Предисловие и полный учебник, для русских», Типо-литография Ч. Кельтера, Варшава, 1887 </li>
        <li>Zamenhof, L. L., “Fundamento de Esperanto, Gramatiko, ekzercaro, universala vortaro”, Hachette et Cie, Paris, 1905</li>
        <li>Заменгоф, Л. Л., «Основы международного языка эсперанто», М., 1911</li>
        <li>А. И Королевич, «Книга об эсперанто», Киев, Наукова думка», 1989</li>
        <li>“Laute!”, 3-1997,  Э. В. Вартанян, «Путешествие в слово», М, Сов. Россия, 1975</li>
        <li>V. Sadler, “Working with Analogical Semantics”, London, 1989</li>
        <li>revuo “Esperanto”, marto 2004, p. 56</li>
        <li>Мирей Гросжан, «Швейцария: мозаика культур, немецких, латинских и…», в: “Novaj edukaj valoroj”, Bennet college, Moskvo, 1997</li>
        <li>U. Eco, “La sercxado de la perfecta lingvo en la europa kulturo”, 1994</li>
        <li>Praga Manifesto</li>
        <li>Мирей Гросжан, «Швейцария: мозаика культур, немецких, латинских и…», в: “Novaj edukaj valoroj”, Bennet college, Moskvo, 1997</li>
        <li>там же</li>
      </ol>
    </div>
    `
  )
}

const itemName = {
  rus: `Эсперанто как языковой феномен`
}

const item = {
  itemlink: getLink(itemName.rus),
  rus: {
    itemName: itemName.rus,
    itemContent: content.rus,
    itemAuthor: author.rus
  }
}

export default item;


  
    

    







